import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ALERT_ERROR } from '../../../store/constants/alertTypes';
import { uploadPricingFile } from '../../../store/actions/api/pricing';
import { usePricingAnalytics } from '../../filters/PricingFilters/usePricingAnalytics';

import { CymbioSpinner } from '../../common/Spinner';
import RetailerIdsListModal from '../RetailerIdsListModal/RetailerIdsListModal';
import { alertSuccess } from '../../../store/actions/alert';
import { uploadFileToPreSignedUrl } from '../../../services/upload';

import styles from './UploadPricingOptionsModal.module.scss';

const SUBMIT_STATUS_IN_PROGRESS = 'SUBMIT_STATUS_IN_PROGRESS';

export function UploadPricingOptionsModal(props) {
  const { onClose } = props;
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const { supplierId, retailerId } = useParams();
  const { reportToAnalytics } = usePricingAnalytics();

  const onSubmit = async () => {
    if (!file) {
      return;
    }

    try {
      setSubmitStatus(SUBMIT_STATUS_IN_PROGRESS);
      const fileType = file.name.split('.').at(-1);

      const isFileTypeValid = ['csv', 'tsv'].includes(fileType);
      if (!isFileTypeValid) {
        dispatch({ type: ALERT_ERROR, payload: `Invalid file type "${fileType}", please upload csv/tsv files only.` });
        return;
      }

      const { path, url } = await uploadFileToPreSignedUrl({
        supplierId,
        file,
        resourceType: 'pricing',
      });

      const payload = {
        data: { path, url },
        file_type: fileType,
        file_name: file.name,
      };

      if (retailerId) {
        payload.retailer_id = Number(retailerId);
      }

      const response = await uploadPricingFile(supplierId, payload);

      dispatch(alertSuccess(response.data.message || 'File uploaded successfully.'));
      reportToAnalytics('upload-prices', { fileName: file.name });
      onClose();
      setFile(null);
    } catch (error) {
      dispatch({ type: ALERT_ERROR, payload: 'Failed to upload file. Please try again later.' });
      reportToAnalytics('upload-prices-failed', { fileName: file.name, errorMessage: error });
    } finally {
      setSubmitStatus(null);
    }
  };

  const getRetailerIdsExample = useMemo(() => {
    return retailerId ? (
      <p>retailer_id = {retailerId}</p>
    ) : (
      <button className={styles.buttonLink} onClick={() => setModalOpen(true)}>
        Retailers IDs list
      </button>
    );
  }, [retailerId]);

  if (submitStatus === SUBMIT_STATUS_IN_PROGRESS) {
    return (
      <div className={styles.wrapper}>
        <CymbioSpinner />
      </div>
    );
  }

  return (
    <>
      <div className={styles.wrapper}>
        <h5>Import Products pricing (TSV/CSV)</h5>
        <label htmlFor="file">Select a file to upload:</label>
        <div className={styles.fileWrapper}>
          <button className={styles.button}>Choose File</button>
          {file && <span className={styles.fileName}>{file.name}</span>}
          <input
            id="file"
            type="file"
            accept=".tsv,.csv"
            onChange={(e) => {
              setFile(e.target.files[0]);
            }}
            ref={inputRef}
          />
        </div>
        <hr />
        <div className={styles.examples}>
          <div className={styles.example}>
            <a
              href="https://docs.google.com/document/d/1hXvbavpdYq62aGW6wnjvNhWAU8R9HXT0FLO2qiv6gVc/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Pricing Tool Documentation
            </a>
          </div>
          {getRetailerIdsExample}
          <div className={styles.example}>
            <a
              href="https://docs.google.com/spreadsheets/d/1G9yQhkZKbPdASjFcIwf7PBAyBDcSj4LtO2egaywrHB8/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Example file for Dropship
            </a>
          </div>
          <div className={styles.example}>
            <a
              href="https://docs.google.com/spreadsheets/d/1_S8QFy2Z6Yf_gMFuF7GMP_srFiGILX7-pku3OSbpAa8/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Example file for Marketplace
            </a>
          </div>
          <p>Please note, price updates might take up to an hour.</p>
        </div>
        <footer>
          <button type="button" className={styles.button} onClick={onClose}>
            Close
          </button>

          <button type="button" className={styles.button} disabled={!file} onClick={onSubmit}>
            Upload
          </button>
        </footer>
      </div>

      <RetailerIdsListModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

UploadPricingOptionsModal.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
